import React, { useState, useEffect } from 'react';
import { Col, FormGroup, Input, Row, Button, Warning, Notification } from '@snsw/react-component-library';
import styles from './MobileWithValidation.module.scss';
import MobileWithValidationService from './MobileWithValidationService';

const MobileWithValidation = (props) => {
  const mainTitle = props.mainTitle; // || 'Please verify your mobile';
  const codeTitle = props.codeTitle; // || 'Enter the code below';
  const description = props.description; // || 'This is to verify that you have access to the nominated mobile account.';
  const mobileLabel = props?.labels?.mobile || 'Mobile number';
  const codeLabel = props?.labels?.code || 'Code';
  const enableSkipVerification = props.enableSkipVerification || false;
  const stepNumber = props.stepNumber || 2;
  const baseUrl = typeof props.baseUrl !== 'undefined' ? props.baseUrl : 'http://localhost:4001';
  const sendCodePath = props.sendCodePath || '/2FA/SendCode';
  const verifyCodePath = props.verifyCodePath || '/2FA/VerifyCode';

  const [skipVerification, setSkipVerification] = useState(props.value?.skipVerification || false);
  const [mobile, setMobile] = useState(props.value?.mobile || '');
  const [code, setCode] = useState(props.value?.code || '');

  const [sendingMobile, setSendingMobile] = useState(false);
  const [sendingMobileSuccess, setSendingMobileSuccess] = useState(false);
  const [sendingMobileError, setSendingMobileError] = useState(false);

  const [sendingCode, setSendingCode] = useState(false);
  const [sendingCodeSuccess, setSendingCodeSuccess] = useState(false);
  const [sendingCodeError, setSendingCodeError] = useState(false);

  const [isValid, setIsValid] = useState(false);
  const [isMobileValid, setIsMobileValid] = useState(true);

  const dispatchChange = (val, mobile) => {
    setIsValid(val);
    if (props.onValidationChange) {
      props.onValidationChange(val);
    }
    if (props.onChange) {
      props.onChange(mobile);
    }
  };

  const isValidMobile = (mobile) => {
    // eslint-disable-next-line no-control-regex
    const re = /^\({0,1}((0|\+61|61)(2|4|3|7|8)){0,1}\){0,1}(\ |-){0,1}[0-9]{2}(\ |-){0,1}[0-9]{2}(\ |-){0,1}[0-9]{1}(\ |-){0,1}[0-9]{3}$/i;
    return !(mobile === null || !re.test(String(mobile).toLowerCase()));
  };

  useEffect(() => {
    if (skipVerification) {
      dispatchChange(isValid, mobile);
    }
  }, [mobile]);

  return (
    <>
      {mainTitle ? <h2>{mainTitle}</h2> : null}
      {description ? <p>{description}</p> : null}
      <Row>
        <Col span={12}>
          <FormGroup
            id="mobile"
            label={mobileLabel}
            errorMessage={
              !isMobileValid
                ? 'The mobile number is not valid or has not been verified.'
                : 'The mobile number is not verified.'
            }
            hasError={!isMobileValid || props.hasError}
          >
            <Input
              placeholder={'Mobile number'}
              disabled={sendingMobile || sendingMobileSuccess}
              value={mobile}
              onChange={(evt) => {
                setMobile(evt.target.value);
              }}
            />
          </FormGroup>
        </Col>
      </Row>
      {!skipVerification ? (
        <Row className={styles.buttonContainer}>
          <Col span={12}>
            {!sendingMobile || sendingMobileError ? (
              <Button
                variant={sendingMobileSuccess ? 'secondary' : 'primary'}
                onClick={() => {
                  const isValid = isValidMobile(mobile);
                  setIsMobileValid(isValid);
                  if (isValid) {
                    setSendingMobile(true);
                    setSendingMobileSuccess(false);
                    setSendingMobileError(false);
                    dispatchChange(false);
                    MobileWithValidationService.validateMobile(baseUrl, sendCodePath, mobile)
                      .then(() => {
                        setSendingMobile(false);
                        setSendingMobileSuccess(true);
                        setSendingMobileError(false);
                      })
                      .catch(() => {
                        setSendingMobile(false);
                        setSendingMobileSuccess(false);
                        setSendingMobileError(true);
                      });
                  }
                }}
              >
                Send code
              </Button>
            ) : null}
          </Col>
        </Row>
      ) : null}

      {!sendingCode && !sendingCodeSuccess && !sendingCodeError && !sendingMobile && sendingMobileSuccess ? (
        <Row>
          <Col span={12}>
            <Notification variant="success" title={`Mobile has been sent`} />
          </Col>
        </Row>
      ) : null}
      {!sendingMobile && sendingMobileError ? (
        <Row>
          <Col span={12}>
            <Notification
              variant="error"
              title={`There was a problem sending the message. Please verify your input is correct.`}
            />
          </Col>
        </Row>
      ) : null}

      {sendingMobileSuccess ? (
        <>
          {codeTitle ? <h2>{codeTitle}</h2> : null}
          <Row>
            <Col span={12}>
              <FormGroup
                id="code"
                label={codeLabel}
                errorMessage="Please enter the code as received in the received mobile."
                hasError={props.hasError}
              >
                <Input
                  placeholder={'Validation Code'}
                  value={code}
                  onChange={(evt) => {
                    setCode(evt.target.value);
                  }}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row className={styles.buttonContainer}>
            <Col span={4}>
              <Button
                onClick={() => {
                  setSendingCode(true);
                  setSendingCodeSuccess(false);
                  setSendingCodeError(false);
                  MobileWithValidationService.validateCode(baseUrl, verifyCodePath, mobile, code)
                    .then(() => {
                      setSendingCode(false);
                      setSendingCodeSuccess(true);
                      setSendingCodeError(false);
                      dispatchChange(true, mobile);
                    })
                    .catch(() => {
                      setSendingCode(false);
                      setSendingCodeSuccess(false);
                      setSendingCodeError(true);
                    });
                }}
              >
                Validate
              </Button>
            </Col>
          </Row>
        </>
      ) : null}
      {!sendingCode && sendingCodeSuccess ? (
        <Row>
          <Col span={12}>
            <Notification variant="success" title={`Code has been successfully validated`} />
          </Col>
        </Row>
      ) : null}
      {!sendingCode && sendingCodeError ? (
        <Row>
          <Col span={12}>
            <Notification variant="error" title={`The entered code is invalid, please try again.`} />
          </Col>
        </Row>
      ) : null}
      {enableSkipVerification && !skipVerification && !sendingCode && !sendingCodeSuccess ? (
        <Row className={styles.linkContainer}>
          <Col span={4}>
            <a
              href={'#'}
              onClick={(e) => {
                e.preventDefault();
                setSkipVerification(true);
                dispatchChange(true);
              }}
            >
              Skip verification
            </a>
          </Col>
        </Row>
      ) : null}
      {skipVerification ? (
        <Row>
          <Col span={12}>
            <Notification
              variant="warning"
              title={`You can proceed with the form, but your mobile hasn't been verified.`}
            />
          </Col>
        </Row>
      ) : null}
    </>
  );
};

export default MobileWithValidation;
